import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Icon } from '../../icons';
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from '../../constants/color';
import { useAuth } from '../../context/authContext';

const TechnicalReports = () => {
  const { userData } = useAuth();
  const [reports, setReports] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState({
    title: '',
    type: 'daily',
    project: '',
    description: '',
    observations: [],
  });

  useEffect(() => {
    // Mock data - replace with actual API calls
    setReports([
      {
        id: 1,
        title: 'Daily Technical Report - Building A',
        type: 'daily',
        project: 'Building A',
        description: 'Daily technical observations and progress report',
        status: 'completed',
        date: '2026-02-03',
        createdBy: 'John Doe',
        observations: 5,
      },
      {
        id: 2,
        title: 'Weekly Technical Summary - Bridge B',
        type: 'weekly',
        project: 'Bridge B',
        description: 'Weekly summary of all technical observations',
        status: 'in-progress',
        date: '2026-02-02',
        createdBy: 'Jane Smith',
        observations: 12,
      },
      {
        id: 3,
        title: 'Monthly Technical Analysis - Tower C',
        type: 'monthly',
        project: 'Tower C',
        description: 'Monthly comprehensive technical analysis',
        status: 'draft',
        date: '2026-02-01',
        createdBy: 'Mike Johnson',
        observations: 25,
      },
    ]);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'daily':
        return 'primary';
      case 'weekly':
        return 'secondary';
      case 'monthly':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleCreateReport = () => {
    // Handle creating new report
    console.log('Creating report:', newReport);
    setCreateDialogOpen(false);
    setNewReport({
      title: '',
      type: 'daily',
      project: '',
      description: '',
      observations: [],
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H1,
            color: COLORS.TEXT,
          }}
        >
          Technical Reports
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<Icon name="plus" size={16} />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: COLORS.PRIMARY,
            '&:hover': {
              backgroundColor: COLORS.SECONDARY,
            },
            fontFamily: FONT_FAMILY,
            textTransform: 'none',
          }}
        >
          Create Report
        </Button>
      </Box>

      {/* Reports Table */}
      <Paper
        sx={{
          backgroundColor: COLORS.BACKGROUND,
          border: `1px solid ${COLORS.BORDER}`,
          borderRadius: 2,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Project
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Observations
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      fontWeight: 500,
                    }}
                  >
                    {report.title}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.type}
                      color={getTypeColor(report.type)}
                      size="small"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {report.project}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {report.description}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.status}
                      color={getStatusColor(report.status)}
                      size="small"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {report.observations}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {report.createdBy}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {report.date}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <Icon name="eye" size={16} color={COLORS.PRIMARY} />
                    </IconButton>
                    <IconButton size="small">
                      <Icon name="edit" size={16} color={COLORS.PRIMARY} />
                    </IconButton>
                    <IconButton size="small">
                      <Icon name="download" size={16} color={COLORS.PRIMARY} />
                    </IconButton>
                    <IconButton size="small">
                      <Icon name="trash" size={16} color={COLORS.ERROR} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Report Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H2,
            color: COLORS.TEXT,
          }}
        >
          Create New Technical Report
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Report Type
                </InputLabel>
                <Select
                  value={newReport.type}
                  onChange={(e) => setNewReport({ ...newReport, type: e.target.value })}
                  sx={{
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  <MenuItem value="daily" sx={{ fontFamily: FONT_FAMILY }}>Daily</MenuItem>
                  <MenuItem value="weekly" sx={{ fontFamily: FONT_FAMILY }}>Weekly</MenuItem>
                  <MenuItem value="monthly" sx={{ fontFamily: FONT_FAMILY }}>Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project"
                value={newReport.project}
                onChange={(e) => setNewReport({ ...newReport, project: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={newReport.description}
                onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            variant="outlined"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateReport}
            variant="contained"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              backgroundColor: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: COLORS.SECONDARY,
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicalReports;
